import React, { useCallback, useMemo, useState } from 'react';
import { RCATablesContainer, TableColumnHeader } from './styled';
import { Retry } from '../common';
import Table from '../common/Table';
import { useSelector } from 'react-redux';
import { chartIntervalSelector } from '@src/store/trends/birdChart/selectors';
import { metricFilterSelector } from '@src/store/trends/base/selectors';
import { filterSelector } from '@src/store/common/selectors';
import { useRequestDefault } from './utils/useRequestDefault';
import { formatRequestColumns, formatRequestData } from './utils/formatColumn';
import {
  EmptyColumnSearchData,
  dimensionRequestDuration,
  dimensionTraceId,
  reqeustDefaultDimensionList,
  requestDefaultFields,
} from './utils/constant';
import { useEventGFWithSF } from './utils/useEventGFWithSF';
import SearchTool from '../app-viewer-sessions/Table/SearchTool';
import Empty from '../app-viewer-sessions/Table/Empty';
import { useSetTableExport } from './utils/useSetTableExport';

// Don't need to filter out invalid dimensions now, because network_request_url_query has been removed,
// and host/path/code are available for all customers.
const filteredDimensionList = [
  ...reqeustDefaultDimensionList,
  dimensionTraceId,
  dimensionRequestDuration,
];

export const RequestTables = () => {
  const interval = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterSelector);
  const filter = useSelector(filterSelector);
  const eventGFWithSF = useEventGFWithSF();
  const [sortBy, setSortBy] = useState({
    key: dimensionRequestDuration,
    order: 'desc',
  });
  const [columnSearchData, setColumnSearchData] = useState(EmptyColumnSearchData);

  const { data, isFetching, refetch, isError } = useRequestDefault(
    interval,
    metricFilter,
    filter,
    filteredDimensionList,
  );
  const columnMetrics = requestDefaultFields.filter((it) => filteredDimensionList.includes(it.key));
  const columns = formatRequestColumns({ metrics: columnMetrics });
  const tableData = formatRequestData({ data, sortBy, columnSearchData });

  useSetTableExport({ interval, columns, data: tableData, type: 'normal' });

  const onColumnSort = ({ key, order }) => {
    setSortBy({ key, order });
  };

  const onSearch = useCallback((data) => {
    if (typeof data.text === 'string' && data.text.length > 0) {
      setColumnSearchData(data);
    } else {
      setColumnSearchData(EmptyColumnSearchData);
    }
  }, []);

  const headerRenderer = useCallback(
    ({ cells, columns: tableColumns }) => {
      return tableColumns.map((item, index) => (
        <TableColumnHeader key={item.title}>
          {cells[index]}
          {item.searchable && (
            <SearchTool
              columnSearchData={columnSearchData}
              currentKey={item.dataKey}
              onSearch={onSearch}
            />
          )}
        </TableColumnHeader>
      ));
    },
    [columnSearchData, onSearch],
  );

  return (
    <RCATablesContainer>
      {eventGFWithSF && data?.length === 0
        ? 'Primary filter from event is not applicable with secondary filter.'
        : 'Showing random 1k network requests for the time range selected.'}
      <Retry
        fetchData={refetch}
        isLoading={isFetching}
        showError={isError}
        isEmpty={data?.length === 0}
        progress={true}
      >
        <Table
          fixed
          columns={columns}
          data={tableData}
          headerHeight={50}
          rowHeight={45}
          sortBy={sortBy}
          onColumnSort={onColumnSort}
          headerRenderer={headerRenderer}
          emptyRenderer={<Empty />}
        />
      </Retry>
    </RCATablesContainer>
  );
};
